import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminApi as api } from "@/lib/api";
import { ExternalLink, Search, RefreshCw, CheckCircle2, Download, Compass, Radar } from "lucide-react";

const WARD_STATUS_STYLE = {
  verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
  imported: "bg-blue-50 text-blue-800 border-blue-200",
  needs_review: "bg-amber-50 text-amber-800 border-amber-200",
  source_changed: "bg-amber-50 text-amber-800 border-amber-200",
  import_failed: "bg-red-50 text-red-800 border-red-200",
  no_open_data: "bg-slate-100 text-slate-600 border-slate-300",
  ward_data_needed: "bg-slate-100 text-slate-600 border-slate-300",
  at_large: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const MATRIX_STYLE = {
  "Scan Failed": "bg-red-50 text-red-800 border-red-200",
  "Needs Verification": "bg-amber-50 text-amber-800 border-amber-200",
  Ready: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Ward Data Missing": "bg-amber-50 text-amber-800 border-amber-200",
  Monitoring: "bg-blue-50 text-blue-800 border-blue-200",
  "Candidate List Not Published": "bg-slate-100 text-slate-700 border-slate-300",
  "Ward Data Loaded": "bg-blue-50 text-blue-800 border-blue-200",
  "Not Yet Monitored": "bg-slate-100 text-slate-500 border-slate-200",
};

const Meter = ({ label, value, sub, testId }) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-2xl font-heading font-bold text-foreground" data-testid={testId}>{value}</p>
      <p className="text-xs font-medium text-foreground mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const WellandWatchCard = () => {
  const [watch, setWatch] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => api.get("/admin/coverage/watch").then((r) => setWatch(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/coverage/watch/run");
      toast.success(`Watch ran: ${r.data.candidates_found} candidate source(s), ${r.data.new_detections} new.`);
      load();
    } catch (e) { toast.error("Watch run failed."); }
    finally { setRunning(false); }
  };
  if (!watch) return null;
  const unread = (watch.notices || []).filter((n) => !n.read);
  return (
    <Card className="mb-6 border-l-4 border-l-amber-400" data-testid="welland-watch-card">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-heading font-bold text-sm text-foreground inline-flex items-center gap-2">
              <Radar className="h-4 w-4 text-accent" /> Welland Watch
              <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 text-[10px]">
                {watch.welland_status.replace("_", " ")}
              </Badge>
              {unread.length > 0 && (
                <Badge variant="outline" className="bg-red-50 text-red-800 border-red-300 text-[10px]"
                       data-testid="welland-watch-alert-badge">
                  {unread.length} new detection{unread.length === 1 ? "" : "s"}
                </Badge>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1 max-w-2xl">
              Daily automatic check (7:00 AM ET) of official open-data indexes for the new Welland ward
              geometry (By-law 2025-90). Detection never auto-verifies — required workflow:{" "}
              <span className="font-semibold text-foreground">{watch.required_workflow}</span>
            </p>
            <p className="text-[10px] text-muted-foreground mt-1" data-testid="welland-watch-last-checked">
              Last checked: {watch.watch.last_checked_at ? new Date(watch.watch.last_checked_at).toLocaleString() : "never"} ·
              Candidate sources found: {(watch.watch.last_found || []).length}
            </p>
            {unread.map((n) => (
              <div key={n.id} className="rounded border border-amber-300 bg-amber-50 p-3 mt-2" data-testid="welland-watch-notice">
                <p className="text-xs font-semibold text-amber-900">{n.title}</p>
                <p className="text-[11px] text-amber-900/90 mt-0.5">{n.body}</p>
                <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px]"
                        onClick={async () => { await api.post(`/admin/coverage/watch/notices/${n.id}/read`); load(); }}>
                  Mark reviewed
                </Button>
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="welland-watch-run-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${running ? "animate-spin" : ""}`} /> Run check now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


export default function AdminCoverage() {
  const [summary, setSummary] = useState(null);
  const [wards, setWards] = useState([]);
  const [wardFilter, setWardFilter] = useState("all");
  const [matrix, setMatrix] = useState([]);
  const [matrixQ, setMatrixQ] = useState("");
  const [matrixStatus, setMatrixStatus] = useState("all");
  const [discover, setDiscover] = useState(null); // {slug, name, results}
  const [importForm, setImportForm] = useState(null); // {slug, name, service_url, source_name}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/coverage/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/admin/coverage/wards", { params: wardFilter !== "all" ? { status: wardFilter } : {} })
      .then((r) => setWards(r.data)).catch(() => {});
  }, [wardFilter]);

  const loadMatrix = useCallback(() => {
    const params = {};
    if (matrixQ.trim()) params.q = matrixQ.trim();
    if (matrixStatus !== "all") params.status = matrixStatus;
    api.get("/admin/coverage/municipalities", { params }).then((r) => setMatrix(r.data)).catch(() => {});
  }, [matrixQ, matrixStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  const runDiscover = async (row) => {
    setDiscover({ slug: row.municipality_slug, name: row.municipality_name, results: null });
    try {
      const r = await api.get("/admin/coverage/wards/discover", { params: { municipality: row.municipality_name } });
      setDiscover({ slug: row.municipality_slug, name: row.municipality_name, results: r.data.results, note: r.data.note });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discovery failed");
      setDiscover(null);
    }
  };

  const runImport = async () => {
    if (!importForm?.service_url || !importForm?.source_name) {
      toast.error("Source URL and source name are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/coverage/wards/import", {
        municipality_slug: importForm.slug,
        service_url: importForm.service_url,
        source_name: importForm.source_name,
        source_page_url: importForm.source_page_url || null,
      });
      if (r.data.ok) {
        toast.success(`Imported ${r.data.ward_count} wards for ${importForm.name}`);
        setImportForm(null);
        setDiscover(null);
        load();
      } else {
        toast.error(`Import failed: ${r.data.error}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (slug, status) => {
    try {
      await api.patch(`/admin/coverage/wards/${slug}`, { status });
      toast.success(`Status set to ${status}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const recheck = async (slug) => {
    try {
      const r = await api.post(`/admin/coverage/wards/${slug}/recheck`);
      if (r.data.ok) toast.success(r.data.changed ? "Source CHANGED — flagged for re-verification" : "Source unchanged");
      else toast.error(`Recheck failed: ${r.data.error}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recheck failed");
    }
  };


  return (
    <div data-testid="admin-coverage">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-foreground">Ontario Coverage</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Province-wide data health. Coverage numbers are admin-only until the underlying data genuinely supports public claims.
        </p>
      </div>

      <WellandWatchCard />

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6" data-testid="coverage-meter">
          <Meter label="Municipalities" value={`${summary.municipalities_tracked} / ${summary.municipalities_total}`} sub="boundary registry" testId="meter-municipalities" />
          <Meter label="Election pages found" value={summary.election_pages_found} testId="meter-election-pages" />
          <Meter label="Candidate sources" value={summary.candidate_sources_monitored} sub="monitored" testId="meter-candidate-sources" />
          <Meter label="Ward boundaries" value={summary.ward_boundaries_loaded} sub={`${summary.ward_boundaries_verified} verified`} testId="meter-ward-boundaries" />
          <Meter label="Candidate lists live" value={summary.candidate_lists_published} sub="published by municipalities" testId="meter-lists-published" />
          <Meter label="Needing review" value={summary.data_requiring_review} sub={`${summary.ward_data_requiring_review} ward · ${summary.imported_candidates_pending} candidates`} testId="meter-review" />
          <Meter label="Sources failing" value={summary.sources_failing} testId="meter-failing" />
        </div>
      )}

      {summary?.status_groups && (
        <Card className="mb-8" data-testid="coverage-status-groups">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h2 className="font-heading font-bold text-base text-foreground">Ward geometry progress</h2>
              <p className="text-xs text-muted-foreground tabular-nums" data-testid="coverage-progress-text">
                {summary.progress.with_ward_geometry} of {summary.progress.total} municipalities have ward geometry
                ({summary.progress.geometry_percent}%) · {summary.progress.verified} verified ({summary.progress.verified_percent}%)
              </p>
            </div>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden" data-testid="coverage-progress-bar" role="progressbar"
                 aria-valuenow={summary.progress.geometry_percent} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full bg-accent" style={{ width: `${summary.progress.geometry_percent}%` }} />
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {summary.status_groups.map((g) => (
                <Badge key={g.group} variant="outline" className="bg-secondary text-[11px]" data-testid={`status-group-${g.group.replace(/\W+/g, "-").toLowerCase()}`}>
                  {g.group}: {g.count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="wards">
        <TabsList>
          <TabsTrigger value="wards" data-testid="tab-ward-queue">Ward boundary queue</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-matrix">Province matrix</TabsTrigger>
        </TabsList>

        <TabsContent value="wards" className="mt-4">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Label className="text-sm">Status</Label>
            <Select value={wardFilter} onValueChange={setWardFilter}>
              <SelectTrigger className="w-56 bg-white" data-testid="ward-queue-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="imported">Imported</SelectItem>
                <SelectItem value="needs_review">Needs Review</SelectItem>
                <SelectItem value="source_changed">Source Changed</SelectItem>
                <SelectItem value="import_failed">Import Failed</SelectItem>
                <SelectItem value="no_open_data">No Open Data Found</SelectItem>
                <SelectItem value="ward_data_needed">Ward Data Needed</SelectItem>
                <SelectItem value="at_large">At-large (no wards)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{wards.length} municipalities</p>
          </div>

          <div className="space-y-2" data-testid="ward-queue-list">
            {wards.slice(0, 100).map((w) => (
              <Card key={w.municipality_slug} data-testid={`ward-row-${w.municipality_slug}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-semibold text-sm text-foreground">{w.municipality_name}</p>
                        <Badge variant="outline" className={`text-[10px] ${WARD_STATUS_STYLE[w.verification_status] || ""}`}>
                          {w.verification_status.replace(/_/g, " ")}
                        </Badge>
                        {w.ward_count ? <span className="text-[11px] text-muted-foreground">{w.ward_count} wards</span> : null}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {w.boundary_source || "No boundary source configured"}
                        {w.last_checked_at ? ` · checked ${new Date(w.last_checked_at).toLocaleDateString("en-CA")}` : ""}
                      </p>
                      <p className="text-[11px] text-amber-700 mt-0.5">Action: {w.action_needed}</p>
                      {w.last_error && <p className="text-[11px] text-red-700 mt-0.5">{w.last_error}</p>}
                      {w.notes && <p className="text-[11px] text-muted-foreground mt-0.5 max-w-2xl">{w.notes}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => runDiscover(w)} data-testid={`discover-${w.municipality_slug}`}>
                        <Compass className="h-3.5 w-3.5 mr-1" /> Discover
                      </Button>
                      <Button size="sm" variant="outline"
                              onClick={() => setImportForm({ slug: w.municipality_slug, name: w.municipality_name, service_url: w.service_url || "", source_name: w.boundary_source || "", source_page_url: w.source_page_url || "" })}
                              data-testid={`import-${w.municipality_slug}`}>
                        <Download className="h-3.5 w-3.5 mr-1" /> Import
                      </Button>
                      {w.has_boundary_file && w.verification_status !== "verified" && (
                        <Button size="sm" onClick={() => setStatus(w.municipality_slug, "verified")} data-testid={`verify-${w.municipality_slug}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark verified
                        </Button>
                      )}
                      {w.service_url && (
                        <Button size="sm" variant="ghost" onClick={() => recheck(w.municipality_slug)} aria-label="Recheck source" data-testid={`recheck-${w.municipality_slug}`}>
                          <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {wards.length > 100 && <p className="text-xs text-muted-foreground">Showing first 100 — filter by status to narrow down.</p>}
          </div>
        </TabsContent>

        <TabsContent value="matrix" className="mt-4">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input className="pl-9 w-64 bg-white" placeholder="Search municipality…" value={matrixQ}
                     onChange={(e) => setMatrixQ(e.target.value)} data-testid="matrix-search" />
            </div>
            <Select value={matrixStatus} onValueChange={setMatrixStatus}>
              <SelectTrigger className="w-64 bg-white" data-testid="matrix-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All statuses</SelectItem>
                {Object.keys(MATRIX_STYLE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="coverage-matrix-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Municipality</th>
                  <th className="px-4 py-3">Election page</th>
                  <th className="px-4 py-3">Candidate source</th>
                  <th className="px-4 py-3">Ward data</th>
                  <th className="px-4 py-3">Candidates</th>
                  <th className="px-4 py-3">Last scan</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {matrix.slice(0, 100).map((r) => (
                  <tr key={r.municipality_slug} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-medium text-foreground whitespace-nowrap">{r.municipality_name}</td>
                    <td className="px-4 py-2.5">
                      {r.election_page ? (
                        <a href={r.election_page} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 text-xs">
                          page <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {r.candidate_source ? (
                        <a href={r.candidate_source} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 text-xs">
                          source <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={`text-[10px] ${WARD_STATUS_STYLE[r.ward_data] || ""}`}>{r.ward_data.replace(/_/g, " ")}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{r.candidates}{r.pending_review ? ` (${r.pending_review} pending)` : ""}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                      {r.last_scan ? new Date(r.last_scan).toLocaleDateString("en-CA") : "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={`text-[10px] whitespace-nowrap ${MATRIX_STYLE[r.status] || ""}`}>{r.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Showing up to 100 rows — use search/status filters for the rest of the 414.</p>
        </TabsContent>
      </Tabs>

      {/* Discovery dialog */}
      <Dialog open={!!discover} onOpenChange={(v) => !v && setDiscover(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Discover ward sources — {discover?.name}</DialogTitle></DialogHeader>
          {!discover?.results && <p className="text-sm text-muted-foreground">Searching ArcGIS Hub…</p>}
          {discover?.results && (
            <>
              <p className="text-xs text-amber-700">{discover.note}</p>
              <div className="space-y-2 mt-2">
                {discover.results.map((r, i) => (
                  <Card key={i}>
                    <CardContent className="p-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{r.title}</p>
                        <p className="text-[11px] text-muted-foreground">
                          Publisher: {r.owner || "unknown"}{" "}
                          {r.official_hint
                            ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-800 border-emerald-200 ml-1">looks official</Badge>
                            : <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-800 border-amber-200 ml-1">verify publisher</Badge>}
                        </p>
                        {r.service_url && <p className="text-[10px] text-muted-foreground break-all mt-1">{r.service_url}</p>}
                      </div>
                      <Button size="sm" variant="outline" disabled={!r.service_url}
                              onClick={() => setImportForm({ slug: discover.slug, name: discover.name, service_url: r.service_url, source_name: `${r.owner || "Unknown"} — ${r.title} (ArcGIS Open Data)`, source_page_url: r.service_url })}>
                        Use
                      </Button>
                    </CardContent>
                  </Card>
                ))}
                {discover.results.length === 0 && <p className="text-sm text-muted-foreground">No datasets found. Consider marking this municipality “No Open Data Found”.</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={!!importForm} onOpenChange={(v) => !v && setImportForm(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Import ward boundaries — {importForm?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Service / GeoJSON URL</Label>
              <Input value={importForm?.service_url || ""} onChange={(e) => setImportForm({ ...importForm, service_url: e.target.value })} data-testid="import-url-input" placeholder="https://…/FeatureServer or direct GeoJSON URL" />
            </div>
            <div>
              <Label className="mb-1.5 block">Source name (citation)</Label>
              <Input value={importForm?.source_name || ""} onChange={(e) => setImportForm({ ...importForm, source_name: e.target.value })} data-testid="import-source-name" placeholder="e.g. City of X Open Data — Ward Boundaries" />
            </div>
            <div>
              <Label className="mb-1.5 block">Source page URL (optional)</Label>
              <Input value={importForm?.source_page_url || ""} onChange={(e) => setImportForm({ ...importForm, source_page_url: e.target.value })} />
            </div>
            <p className="text-[11px] text-amber-700">Only import from clearly-official municipal/regional publishers. The importer validates geometry and ward labels, and never invents wards.</p>
            <Button onClick={runImport} disabled={busy} className="w-full" data-testid="import-run-button">
              {busy ? "Importing…" : "Import boundaries"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
